<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateContract',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    rentalId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mapDefaultContract = (...args) => {
    const data = args[0];
    return _.extend(data, {
      counterparty:
        data.counterparty && data.counterparty.id ? data.counterparty : null,
      company: data.company && data.company.id ? data.company : null,
      rental: data.rental && data.rental.id ? data.rental : null,
    });
  };

  const contract_type = ref('rental');
  const contract = ref(null);
  const contractTypes = ref(null);
  const formName = ref('contract');
  const formId = ref(null);
  const labelAlign = ref('left');
  const currentTab = ref(0);
  const categories = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    contract_type: contract_type.value,
    contract: contract.value,
    contractTypes: contractTypes.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    currentTab: currentTab.value,
    categories: categories.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_contract = () => {
    return $fn.value
      .fetch('read_contract', {
        parameters: {
          id: 'defaults',
          type: $data.value.contract_type,
          rental: props.rentalId,
        },
      })
      .then((data) => mapDefaultContract(data));
  };
  const $$load_contractTypes = () => {
    return $fn.value.getEnumValuesFromSchema('ContractTypes');
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_categories = () => {
    return $fn.value.fetch('list_contract_category');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      {
        name: 'contract_type',
        cache: { type: 'local', key: 'CreateContract.contractType' },
      },
      { name: 'contract' },
      { name: 'contractTypes' },
      { name: 'formName', dependsOn: ['contract'] },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'currentTab' },
      { name: 'categories' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      contract: $$load_contract,
      contractTypes: $$load_contractTypes,
      formId: $$load_formId,
      categories: $$load_categories,
    },
    ux: {},
    dataRefs: {
      contract_type,
      contract,
      contractTypes,
      formName,
      formId,
      labelAlign,
      currentTab,
      categories,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const companyTranslation = computed(() => {
    if (loaded.value) {
      return ['rental', 'rental_agreement', 'parking'].indexOf(
        contract.value.contract_type
      ) > -1
        ? $fn.value.pgettext('Contract', 'Lessor')
        : $fn.value.pgettext('Contract', 'Company');
    }
    return null;
  });
  const counterpartyTranslation = computed(() => {
    if (loaded.value) {
      return ['rental', 'rental_agreement', 'parking'].indexOf(
        contract.value.contract_type
      ) > -1
        ? $fn.value.pgettext('Contract', 'Tenant')
        : $fn.value.pgettext('Contract', 'Counterparty');
    }
    return null;
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Contract') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_d8f0u89lb_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_setData_rlqf74qtu_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract',
      value: mapDefaultContract($event.data.response.data),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_request_u5k380wbt_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_contract',
      parameters: {
        id: 'defaults',
        type: $event.data.value,
      },
    };
  };
  const act_3322283983417540_setData_w8sfhohnn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'currentTab',
      value: currentTab.value - 1,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8328328512915885_setData_3io6wptp8_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'currentTab',
      value: currentTab.value + 1,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2132444698984839_reloadGrid_u0x6ozc8z_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'contracts',
    };
  };
  const act_2132444698984839_closeComponent_hmb0g1tcr_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_2132444698984839_navigate_diuifctmy_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.contract',
        params: {
          id: $event.data.id,
        },
      },
    };
  };
  const act_2132444698984839_crud_gmldz4qym_getActionArgs = async ($event) => {
    return {
      objectType: 'contract',
      op: 'upsert',
      data: contract.value,
    };
  };

  const act_7315092382398562_closeModal_d8f0u89lb =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_d8f0u89lb_getActionArgs,
    });
  const inst_evh_7315092382398562_close_vqnlnx6fk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_d8f0u89lb,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_vqnlnx6fk],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_qhewvcr05 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tmpoj3hh8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qhewvcr05,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tmpoj3hh8],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_d06io7dg1 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_gw8eafbff =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_d06io7dg1,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_gw8eafbff],
    event: 'close',
  });
  const act_7789867021953041_setData_rlqf74qtu = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_rlqf74qtu_getActionArgs,
  });
  const inst_evh_7789867021953041_success_th1qnt28y =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_rlqf74qtu,
      event: 'success',
      displayName: 'setData',
    });
  const act_7789867021953041_request_u5k380wbt = new actionTypes.RequestAction({
    actionArgs: act_7789867021953041_request_u5k380wbt_getActionArgs,
    events: [inst_evh_7789867021953041_success_th1qnt28y],
  });
  const inst_evh_7789867021953041_optionSelected_mvykpm4p3 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_request_u5k380wbt,
      event: 'optionSelected',
      displayName: 'request',
    });
  const inst_evh_7789867021953041_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7789867021953041_optionSelected_mvykpm4p3],
      event: 'optionSelected',
    });
  const act_3322283983417540_setData_w8sfhohnn = new actionTypes.SetDataAction({
    actionArgs: act_3322283983417540_setData_w8sfhohnn_getActionArgs,
  });
  const inst_evh_3322283983417540_clickPreventStop_jd6blq9ol =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_setData_w8sfhohnn,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_3322283983417540_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3322283983417540_clickPreventStop_jd6blq9ol],
      event: 'click.prevent.stop',
    });
  const act_8328328512915885_setData_3io6wptp8 = new actionTypes.SetDataAction({
    actionArgs: act_8328328512915885_setData_3io6wptp8_getActionArgs,
  });
  const inst_evh_8328328512915885_clickPreventStop_9aw4k3m5f =
    new actionTypes.EventHandlerImpl({
      action: act_8328328512915885_setData_3io6wptp8,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_8328328512915885_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8328328512915885_clickPreventStop_9aw4k3m5f],
      event: 'click.prevent.stop',
    });
  const act_2132444698984839_reloadGrid_u0x6ozc8z =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2132444698984839_reloadGrid_u0x6ozc8z_getActionArgs,
    });
  const inst_evh_2132444698984839_success_rsdqwy7kz =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_reloadGrid_u0x6ozc8z,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_2132444698984839_closeComponent_hmb0g1tcr =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2132444698984839_closeComponent_hmb0g1tcr_getActionArgs,
    });
  const inst_evh_2132444698984839_success_ywzicr1dt =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_closeComponent_hmb0g1tcr,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2132444698984839_navigate_diuifctmy =
    new actionTypes.NavigateAction({
      actionArgs: act_2132444698984839_navigate_diuifctmy_getActionArgs,
    });
  const inst_evh_2132444698984839_success_xdkmusb39 =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_navigate_diuifctmy,
      event: 'success',
      displayName: 'navigate',
    });
  const act_2132444698984839_crud_gmldz4qym = new actionTypes.CRUDAction({
    actionArgs: act_2132444698984839_crud_gmldz4qym_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_2132444698984839_success_rsdqwy7kz,
      inst_evh_2132444698984839_success_ywzicr1dt,
      inst_evh_2132444698984839_success_xdkmusb39,
    ],
  });
  const inst_evh_2132444698984839_clickPreventStop_l91lg2rk7 =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_crud_gmldz4qym,
      event: 'click.prevent.stop',
      displayName: 'save',
    });
  const inst_evh_2132444698984839_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2132444698984839_clickPreventStop_l91lg2rk7],
      event: 'click.prevent.stop',
    });
  const act_2076709591253261_closeModal_1e1vcyav2 =
    new actionTypes.CloseModalAction({});
  const inst_evh_2076709591253261_clickPreventStop_l0d5tikqq =
    new actionTypes.EventHandlerImpl({
      action: act_2076709591253261_closeModal_1e1vcyav2,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2076709591253261_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2076709591253261_clickPreventStop_l0d5tikqq],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_vqnlnx6fk,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tmpoj3hh8,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_gw8eafbff,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_success_th1qnt28y,
    inst_evh_7789867021953041_optionSelected_mvykpm4p3,
    inst_evh_7789867021953041_optionSelected,
    inst_evh_3322283983417540_clickPreventStop_jd6blq9ol,
    inst_evh_3322283983417540_clickPreventStop,
    inst_evh_8328328512915885_clickPreventStop_9aw4k3m5f,
    inst_evh_8328328512915885_clickPreventStop,
    inst_evh_2132444698984839_success_rsdqwy7kz,
    inst_evh_2132444698984839_success_ywzicr1dt,
    inst_evh_2132444698984839_success_xdkmusb39,
    inst_evh_2132444698984839_clickPreventStop_l91lg2rk7,
    save: inst_evh_2132444698984839_clickPreventStop_l91lg2rk7,
    inst_evh_2132444698984839_clickPreventStop,
    inst_evh_2076709591253261_clickPreventStop_l0d5tikqq,
    inst_evh_2076709591253261_clickPreventStop,
  };

  const evh_7315092382398562_close_vqnlnx6fk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_vqnlnx6fk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tmpoj3hh8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tmpoj3hh8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_gw8eafbff = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_gw8eafbff.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_th1qnt28y = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_th1qnt28y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_mvykpm4p3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_mvykpm4p3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop_jd6blq9ol = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop_jd6blq9ol.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8328328512915885_clickPreventStop_9aw4k3m5f = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8328328512915885_clickPreventStop_9aw4k3m5f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8328328512915885_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8328328512915885_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_success_rsdqwy7kz = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_success_rsdqwy7kz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_success_ywzicr1dt = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_success_ywzicr1dt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_success_xdkmusb39 = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_success_xdkmusb39.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_clickPreventStop_l91lg2rk7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2132444698984839_clickPreventStop_l91lg2rk7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop_l0d5tikqq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop_l0d5tikqq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateContract"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="px-3">
        <ItpFormGroup
          name="contract_type"
          :label="gettext('Contract type')"
          row
          :label-cols-md="3"
          :content-cols-md="4"
          required
        >
          <ItpFormSearchableSelect
            name="contract_type"
            v-model="contract_type"
            :options="contractTypes"
            valueField="value"
            textField="text"
            required
            @optionSelected="evh_7789867021953041_optionSelected($event, {})"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
      </div>
      <hr />
      <ItpForm :id="formId" :name="formName" class="px-3">
        <BTabs v-model:index="currentTab" vertical pills contentClass="col">
          <BTab :title="pgettext('Contract', 'General')" class="px-3">
            <FieldsetContractNumbersData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractNumbersData>
            <hr />
            <FieldsetContractValidityData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractValidityData>
          </BTab>
          <BTab :title="companyTranslation" class="px-3">
            <FieldsetContractCompanyData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractCompanyData>
          </BTab>
          <BTab :title="counterpartyTranslation" class="px-3">
            <FieldsetContractClientData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractClientData>
          </BTab>
          <BTab :title="pgettext('Contract', 'Rental')" class="px-3">
            <FieldsetContractRentalData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractRentalData>
          </BTab>
          <BTab :title="pgettext('Contract', 'Financial')" class="px-3">
            <FieldsetContractInvoicing
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractInvoicing>
          </BTab>
        </BTabs>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="me-auto">
          <ItpButton
            :text="gettext('Previous')"
            :disabled="currentTab == 0"
            @click.prevent.stop="
              evh_3322283983417540_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
        <ItpBox :gap="2">
          <ItpButton
            :text="gettext('Next')"
            :disabled="currentTab > 3"
            @click.prevent.stop="
              evh_8328328512915885_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            type="submit"
            :form="formId"
            variant="primary"
            icon="fa-save"
            :text="gettext('Create contract')"
            :spinning="actions.save.isRunning"
            :disabled="!forms[formName] || !forms[formName].submittable"
            @click.prevent.stop="
              evh_2132444698984839_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            variant="default"
            :text="gettext('Close')"
            :disabled="actions.save.isRunning"
            @click.prevent.stop="
              evh_2076709591253261_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
